/**
 * One lesson: a rail of steps on the left, the current step on the right.
 *   discover — the book's questions one at a time; each answered question stays on screen with the sentence it earned,
 *              so the student can see the path they took (literal learners lean on this).
 *   practice — generated problems until mastery; a run of misses sends the student back to the discovery.
 *   tables   — opens the Times Tables unit inside the lesson.
 * Feedback: "Not yet." plus the response to that mistake, never the answer; after the ladder, the way is shown.
 * Timing is recorded, never displayed.
 */
import type { Store, Student } from "../data/store";
import { lessonRecord, skillRecord, unitDone, unitSkipped, localDate, type StudentProgress } from "../engine/progress";
import { unitById } from "../engine/curriculum";
import { respond, newBlock, recordResult, levelFor, rule, type BlockState } from "../lessons/core";
import { skillById } from "../lessons/skills";
import type { Item, Lesson, Step } from "../lessons/types";
import { mathMap } from "../lessons/visuals";
import { esc } from "./keypad";
import { mountLesson } from "./lesson";
import { verticalWork } from "./vertical";
import { buildInput } from "./inputs";
import { isRight } from "../lessons/core";
import { DOMAINS, DOMAIN_NAMES } from "../lessons/level3/checkups";
import { PROBE_LESSONS, afterFacts, afterProbe, factItems, probeItems, startState } from "../lessons/placement";
import { FORM_OF, factsSummary, formItems, nextWhen, taken, type When } from "../lessons/factsTest";
import { TEACHING, anyLesson, lessonNo, lessonName } from "../lessons/levels";
import type { CheckItem } from "../lessons/types";

export interface LessonViewOptions {
  store: Store;
  student: Student;
  progress: StudentProgress;
  lesson: Lesson;
  preview: boolean;
  onBack: () => void;
  /** A level below the student's own (Meliesa, Nov 1): the lesson opens with "show me what you know", so the
   *  student does only the parts they need before moving up. */
  catchUp?: boolean;
  /** Open the next lesson in the student's sequence (shown when this one is finished). */
  onNext?: () => void;
  /** Class mode: no "already know this? show me" — the class does each lesson together (Meliesa, Nov 4, 2026). */
  noCheckOut?: boolean;
}

export const stepDone = (p: StudentProgress, lesson: Lesson, s: Step): boolean =>
  s.kind === "tables" ? s.units.every((u) => unitDone(p, u) || unitSkipped(p, u)) : lessonRecord(p, lesson.id).done.includes(s.id);

export const lessonProgress = (p: StudentProgress, lesson: Lesson): { done: number; total: number } => {
  const real = lesson.steps.filter((s) => s.kind !== "soon");
  return { done: real.filter((s) => stepDone(p, lesson, s)).length, total: real.length };
};

const plainHtml = (html: string): string => {
  const d = document.createElement("div");
  d.innerHTML = html;
  // Fractions are two stacked spans — read them "3 over 4", not "34". Exponents: "to the 4th".
  d.querySelectorAll(".frac").forEach((f) => { const [a, b] = [...f.children].map((c) => c.textContent ?? ""); f.replaceWith(` ${a} over ${b} `); });
  d.querySelectorAll("sup").forEach((e) => e.replaceWith(` to the ${e.textContent === "2" ? "second" : e.textContent === "3" ? "third" : `${e.textContent}th`} power `));
  d.querySelectorAll("svg, .sayit, button").forEach((e) => e.remove());
  return (d.textContent ?? "").replace(/\s+/g, " ").trim();
};
const SUPS = "⁰¹²³⁴⁵⁶⁷⁸⁹";
const ordinal = (n: string) => (n === "2" ? "second" : n === "3" ? "third" : `${n}th`);
/** Text for speech. Unicode exponents (x⁴, 10⁻³) read "to the 4th power", "to the negative 3rd power". */
const plain = (html: string): string =>
  plainHtml(html).replace(/(⁻?)([⁰¹²³⁴⁵⁶⁷⁸⁹]+)/g, (_m, neg: string, d: string) => ` to the ${neg ? "negative " : ""}${ordinal([...d].map((c) => SUPS.indexOf(c)).join(""))} power `);


/**
 * The most human-sounding English voice this device has. Devices ship several; the default is often the oldest,
 * most robotic one. Neural voices ("Natural", "Premium", "Enhanced", Google's) sound like a person reading.
 */
const VOICE_RANK: RegExp[] = [/natural/i, /neural/i, /premium/i, /enhanced/i, /^Google US English/i, /\b(Ava|Samantha|Allison|Zoe|Susan|Aria|Jenny|Emma)\b/i, /^Google/i];
let bestVoice: SpeechSynthesisVoice | null = null;
function pickVoice(): SpeechSynthesisVoice | null {
  if (bestVoice) return bestVoice;
  try {
    const vs = speechSynthesis.getVoices().filter((v) => /^en(-|_|$)/i.test(v.lang));
    const score = (v: SpeechSynthesisVoice) => { const i = VOICE_RANK.findIndex((r) => r.test(v.name)); return (i < 0 ? 99 : i) * 2 + (/^en-US/i.test(v.lang) ? 0 : 1); };
    bestVoice = vs.sort((a, b) => score(a) - score(b))[0] ?? null;
  } catch { bestVoice = null; }
  return bestVoice;
}
try { speechSynthesis.addEventListener("voiceschanged", () => { bestVoice = null; pickVoice(); }); } catch { /* no speech */ }

/** Read aloud (K–2 assume an adult reads; the app reads for anyone who wants it). */
function speak(text: string) {
  try {
    const u = new SpeechSynthesisUtterance(text.replace(/−/g, " minus ").replace(/×/g, " times ").replace(/÷/g, " divided by ").replace(/__+/g, " blank ").replace(/\|(\w)\|/g, "the absolute value of $1"));
    const v = pickVoice();
    if (v) { u.voice = v; u.lang = v.lang; }
    u.rate = 0.95;
    u.pitch = 1;
    speechSynthesis.cancel();
    speechSynthesis.speak(u);
  } catch {
    /* no speech on this device */
  }
}

const SAYIT = `<button class="sayit" type="button" aria-label="Read this aloud">🔊</button>`;
/** One listener per root: any 🔊 reads the line it sits in; every intro paragraph gets one. */
function wireSayIt(root: HTMLElement) {
  if (root.dataset.sayit) return;
  root.dataset.sayit = "1";
  root.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest(".sayit");
    if (!b) return;
    const li = b.closest("li"), part = (sel: string) => plain(li?.querySelector(sel)?.innerHTML ?? "").trim();
    if (li && li.querySelector(".tr")) speak([part(".tq"), part(".ta") && `You said ${part(".ta")}.`, part(".tr")].filter(Boolean).join(" … "));
    else speak(plain((b.closest("li, .intro") ?? b.parentElement)!.innerHTML));
  });
  new MutationObserver(() => root.querySelectorAll("p.intro:not(.hasSay)").forEach((p) => { p.classList.add("hasSay"); p.insertAdjacentHTML("beforeend", ` ${SAYIT}`); })).observe(root, { childList: true, subtree: true });
}

export function mountLessonView(root: HTMLElement, o: LessonViewOptions): void {
  wireSayIt(root);
  const { store, student, lesson } = o;
  const P = o.progress;
  const rec = lessonRecord(P, lesson.id);
  const save = () => void store.saveProgress(student.id, P).catch((e) => console.warn("save failed", e));
  const $ = <T extends HTMLElement = HTMLElement>(id: string) => root.querySelector<T>(`#${id}`)!;

  const firstOpen = () => lesson.steps.find((s) => s.kind !== "soon" && !stepDone(P, lesson, s)) ?? lesson.steps[0]!;
  let current: Step = lesson.steps.find((s) => s.id === rec.at) ?? firstOpen();

  root.innerHTML = `
  <header class="band"><div class="wrap">
    <div><div class="brand">THE TIGARD METHOD<sup>™</sup></div><div class="tagline">Pattern · Logic · Discovery</div></div>
    <div class="who"><span>${esc(student.firstName)}</span><button type="button" id="back">All lessons</button></div>
  </div></header>
  <main><div class="wrap">
    <nav aria-label="Steps in this lesson">
      <div class="eyebrow">${lesson.level === "all" ? "Placement" : `Level ${lesson.level} · ${lesson.steps[0]?.kind === "check" ? "Check-Up" : `${lessonName(lesson)}`}`}</div>
      <h2 style="margin:4px 0 12px">${esc(lesson.title)}</h2>
      <ol class="units steps" id="rail"></ol>
      <div id="coBox"></div>
    </nav>
    <section class="lesson" id="stage" aria-live="polite"></section>
  </div></main>
  <footer><div class="wrap">The Tigard Method™ · © 2026 Meliesa Tigard</div></footer>`;
  $("back").onclick = o.onBack;

  function renderRail() {
    const box = $("rail");
    box.innerHTML = "";
    lesson.steps.forEach((s, i) => {
      const li = document.createElement("li");
      const b = document.createElement("button");
      b.type = "button";
      b.className = "unit";
      b.setAttribute("aria-current", String(s.id === current.id));
      const done = s.kind !== "soon" && stepDone(P, lesson, s);
      b.innerHTML = `<span>${i + 1}. ${esc(s.title)}</span><span class="pct">${s.kind === "soon" ? "coming" : done ? "✓" : ""}</span>`;
      b.onclick = () => go(s);
      li.appendChild(b);
      box.appendChild(li);
    });
  }

  /* ---------- "Show me you know this": skip what the student already knows ---------- */
  // Practice steps are checked (two problems each, no hints, at the climbing level); a step passes on both right the
  // first time. A discovery step is skipped when every practice step that backs up to it passed (or, if none does,
  // when every practice step passed). Times-tables units are never skipped — Tigard Times is taught as is.
  const checkable = () => lesson.steps.filter((s): s is Extract<Step, { kind: "practice" }> => s.kind === "practice" && !stepDone(P, lesson, s));
  function renderCheckOut() {
    const box = $("coBox");
    const open = checkable();
    if (o.noCheckOut || lesson.steps[0]?.kind === "check" || lesson.steps[0]?.kind === "placement" || !open.length) { box.innerHTML = ""; return; }
    box.innerHTML = `<button class="btn ghost small" id="coGo" type="button" style="margin-top:12px;width:100%">Already know this? Show me</button>${rec.checkOut ? `<p class="note">Tried ${esc(rec.checkOut.date)}: skipped ${rec.checkOut.passed.length} step${rec.checkOut.passed.length === 1 ? "" : "s"}.</p>` : ""}`;
    $("coGo").onclick = () => drawCheckOut(open);
  }
  function drawCheckOut(steps: Extract<Step, { kind: "practice" }>[]) {
    const box = $("stage");
    const items: { step: string; item: Item }[] = [];
    for (const s of steps) {
      const sk = skillById(s.skill), seen = new Set<string>();
      for (let k = 0, got = 0; k < 30 && got < 2; k++) { const it = sk.gen(Math.random, 1); if (!seen.has(it.id)) { seen.add(it.id); items.push({ step: s.id, item: it }); got++; } }
    }
    const right: Record<string, number> = {};
    let i = 0;
    box.innerHTML = `<div><div class="eyebrow">Show me you know this</div><h1>${esc(lesson.title)}</h1></div>
      <p class="intro">${o.catchUp && !rec.checkOut ? "First, show me what you already know here. A few problems, no hints. Whatever you show, you skip — you'll only do the parts you need." : "A few problems from this lesson, no hints. Whatever you show you already know, you skip. What you don't know yet is what the lesson is for."}</p>
      <div class="dots" id="dots"></div><div class="card" id="cur"></div>
      <div class="row"><button class="btn ghost small" id="skip" type="button">I don't know this one yet</button><span class="note" id="qn" style="margin:0"></span></div>`;
    const cur = $("cur");
    const end = () => {
      const passed: string[] = steps.filter((s) => (right[s.id] ?? 0) === 2).map((s) => s.id);
      const allPassed = passed.length === steps.length;
      for (const d of lesson.steps) {
        if (d.kind !== "discover" || rec.done.includes(d.id)) continue;
        const backs = lesson.steps.filter((x) => x.kind === "practice" && x.backup === d.id);
        const ok = backs.length ? backs.every((x) => passed.includes(x.id) || rec.done.includes(x.id)) : allPassed;
        if (ok) passed.push(d.id);
      }
      for (const id of passed) if (!rec.done.includes(id)) rec.done.push(id);
      rec.checkOut = { date: localDate(), passed };
      const first = firstOpen();
      rec.at = first.id;
      current = first;
      save();
      renderRail();
      renderCheckOut();
      const left = lesson.steps.filter((x) => x.kind !== "soon" && !stepDone(P, lesson, x));
      box.innerHTML = `<div><div class="eyebrow">Show me you know this</div><h1>${left.length ? "Here's where you start" : "You already know this lesson"}</h1></div>
        <div class="card"><p style="margin-top:0">${passed.length ? `You skipped ${passed.length} step${passed.length === 1 ? "" : "s"}.` : "Nothing skipped this time — the lesson will teach it."}</p>
        ${left.length ? `<p class="muted" style="margin-bottom:0">Still to do: ${left.map((x) => esc(x.title)).join(" · ")}</p>` : ""}</div>
        <div class="row">${left.length ? `<button class="btn" id="startAt" type="button">Start: ${esc(first.title)}</button>` : o.onNext ? `<button class="btn" id="nextLesson" type="button">Next lesson</button>` : ""}<button class="btn ghost" id="toAll" type="button">Back to all lessons</button></div>`;
      root.querySelector<HTMLButtonElement>("#startAt")?.addEventListener("click", () => go(first));
      root.querySelector<HTMLButtonElement>("#nextLesson")?.addEventListener("click", () => o.onNext!());
      $("toAll").onclick = o.onBack;
    };
    const one = () => {
      if (i >= items.length) return end();
      const { step, item } = items[i]!;
      $("qn").textContent = `Question ${i + 1} of ${items.length}`;
      $("dots").innerHTML = items.map((_, k) => `<i class="${k < i ? "y" : ""}"></i>`).join("");
      askItem(item, cur, { stepId: "checkout", practice: false, check: true, onDone: (r) => { if (r.correct) right[step] = (right[step] ?? 0) + 1; i++; one(); } });
    };
    $("skip").onclick = () => { if (i < items.length) { i++; one(); } };
    one();
  }

  function go(s: Step) {
    current = s;
    rec.at = s.id;
    save();
    renderRail();
    draw();
    window.scrollTo({ top: 0, behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  }

  function finish(s: Step) {
    if (!rec.done.includes(s.id)) rec.done.push(s.id);
    save();
    renderRail();
    renderCheckOut();
  }

  const next = (s: Step): Step | undefined => lesson.steps[lesson.steps.indexOf(s) + 1];

  function nextButton(s: Step): string {
    const n = next(s);
    return n ? `<button class="btn" id="nextStep" type="button">Next: ${esc(n.title)}</button>` : `<button class="btn" id="nextStep" type="button">Back to all lessons</button>`;
  }
  function wireNext(s: Step) {
    const b = root.querySelector<HTMLButtonElement>("#nextStep");
    if (!b) return;
    const n = next(s);
    b.onclick = () => (n ? go(n) : o.onBack());
  }

  function draw() {
    const s = current, box = $("stage");
    const head = `<div>${lesson.steps.length > 1 ? `<div class="eyebrow">Step ${lesson.steps.indexOf(s) + 1} of ${lesson.steps.length}</div>` : ""}<h1>${esc(s.title)}</h1></div>`;
    if (s.kind === "soon") {
      box.innerHTML = `${head}<div class="card"><p class="muted" style="margin:0">${esc(s.note)}</p></div><div class="row">${nextButton(s)}</div>`;
      wireNext(s);
      return;
    }
    if (s.kind === "tables") {
      const rows = s.units.map((u) => `<li>${esc(unitById(u).name)} — ${unitDone(P, u) ? '<span class="check">done ✓</span>' : unitSkipped(P, u) ? '<span class="check">skipped — solid on the placement check</span>' : `stage ${(P.units[u]?.stage ?? 0) + 1}`}</li>`).join("");
      box.innerHTML = `${head}${s.intro ? `<p>${esc(s.intro)}</p>` : ""}<div class="card"><ul style="margin:0 0 12px">${rows}</ul>
        <button class="btn" id="openTables" type="button">Open the practice</button></div><div class="row">${nextButton(s)}</div>`;
      $("openTables").onclick = () => {
        P.currentUnit = s.units.find((u) => !unitDone(P, u)) ?? s.units[0]!;
        save();
        mountLesson(root, { store, student, progress: P, preview: o.preview, backLabel: "Back to the lesson", onBack: () => mountLessonView(root, o) });
        window.scrollTo({ top: 0 });
      };
      wireNext(s);
      return;
    }
    if (s.kind === "discover") return drawDiscover(s, box, head);
    if (s.kind === "check") return drawCheck(s, box, head);
    if (s.kind === "placement") return drawPlacement(box, head);
    if (s.kind === "factstest") return drawFactsTest(box, head);
    return drawPractice(s, box, head);
  }

  /* ---------- One item: prompt, visual, input, feedback ---------- */
  interface ItemResult { correct: boolean; firstTry: boolean }
  function askItem(item: Item, host: HTMLElement, ctx: { stepId: string; onDone: (r: ItemResult) => void; practice: boolean; check?: boolean; lead?: () => string }) {
    let tries = 0, hintsSeen = 0, start = performance.now(), answered = false;
    const tap = item.input.kind === "tap" ? item.input : null;
    host.innerHTML = `<div class="item">
      <div class="qline"><p class="q">${item.prompt}</p><button class="btn ghost small say" type="button" aria-label="Read the question aloud">🔊 Read to me</button></div>
      <div class="vis" id="vis">${tap ? mathMap({ rows: tap.rows, ring: tap.start ?? [] }) : item.visual ?? ""}</div>
      <div class="ans" id="ansBox"></div>
      <p class="fb" id="fb" role="status"></p>
      <div class="row" id="after"></div></div>`;
    const fb = host.querySelector<HTMLElement>("#fb")!, ansBox = host.querySelector<HTMLElement>("#ansBox")!, after = host.querySelector<HTMLElement>("#after")!;
    // Phones: the numeric keypad has no minus key. In Levels 6–8 (integers, coordinates, decimals) every answer space
    // gets the full keyboard — the same for every item, so the keyboard never hints at the answer's sign.
    // Levels 3–5: the decimal pad (digits and a point) everywhere, so Level 5 decimals can be typed — again the same for every item.
    { const lv = Number(lesson.level), mode = lv >= 6 && lv <= 8 ? "text" : "decimal";
      setTimeout(() => ansBox.querySelectorAll<HTMLInputElement>("input").forEach((b) => { if (b.inputMode === "numeric") b.inputMode = mode; }), 0); }
    host.querySelector<HTMLButtonElement>(".say")!.onclick = () => { const lead = ctx.lead?.() ?? ""; speak(`${lead ? `${lead} … ` : ""}${item.speak ?? plain(item.prompt)}`); };

    const log = (response: string, correct: boolean, hintLevel: number, misconception: string | null) =>
      void store.logSkillAttempt({ studentId: student.id, lessonId: lesson.id, stepId: ctx.stepId, skill: item.skill, itemId: item.id, response, correct, firstTry: correct && tries === 0 && hintsSeen === 0, hintLevel, misconception, ms: performance.now() - start }).catch(() => {});

    const done = (correct: boolean) => {
      answered = true;
      ctx.onDone({ correct, firstTry: correct && tries === 0 && hintsSeen === 0 });
    };

    const submit = (raw: string) => {
      if (answered || !raw.trim()) return;
      if (ctx.check) {
        // Check-up: no feedback, no hints. Record and move on.
        const ok = isRight(item, raw);
        log(raw, ok, 0, item.diagnose?.(raw)?.code ?? null);
        lockInputs();
        done(ok);
        return;
      }
      const r = respond(item, raw, tries);
      log(raw, r.correct, r.hintLevel, r.misconception?.code ?? null);
      if (r.correct) {
        fb.className = "fb good";
        fb.innerHTML = r.text;
        lockInputs();
        done(true);
        return;
      }
      tries++;
      hintsSeen = Math.max(hintsSeen, r.hintLevel);
      fb.className = "fb warn";
      fb.innerHTML = esc(r.text);
      if (r.showWay) {
        if (ctx.practice) {
          // The way is shown; this one doesn't count, and a fresh problem of the same kind follows.
          lockInputs();
          after.innerHTML = `<button class="btn" type="button" id="another">Try another one</button>`;
          after.querySelector<HTMLButtonElement>("#another")!.onclick = () => done(false);
          after.querySelector<HTMLButtonElement>("#another")!.focus();
        } else {
          fb.innerHTML = `${esc(r.text)} <span class="muted">Now you put it in.</span>`;
        }
      }
      focusInput();
    };

    let inputs: (HTMLInputElement | HTMLButtonElement)[] = [];
    let lockInputs = () => inputs.forEach((i) => (i.disabled = true));
    const focusInput = () => inputs.find((i) => !i.disabled && i instanceof HTMLInputElement)?.focus();

    const built = buildInput(item.input, () => submit(built!.value()));
    if (built) {
      ansBox.innerHTML = "";
      ansBox.appendChild(built.el);
      const row = document.createElement("div");
      row.className = "row";
      row.innerHTML = `<button class="btn" type="button">${ctx.check ? "Next" : "Check"}</button>`;
      ansBox.appendChild(row);
      const btn = row.querySelector("button")!;
      btn.onclick = () => submit(built.value());
      inputs = [btn];
      const lock0 = lockInputs;
      lockInputs = () => { lock0(); built.lock(); };
      setTimeout(() => built.focus(), 0);
    } else if (item.input.kind === "vertical") {
      const vw = verticalWork(item.input.top, item.input.bottom, item.input.op);
      ansBox.innerHTML = "";
      ansBox.appendChild(vw.el);
      const row = document.createElement("div");
      row.className = "row";
      row.innerHTML = `<button class="btn" type="button">${ctx.check ? "Next" : "Check"}</button>`;
      ansBox.appendChild(row);
      const btn = row.querySelector("button")!;
      const go = () => submit(vw.value());
      btn.onclick = go;
      vw.onSubmit(go);
      inputs = [btn];
      const lock0 = lockInputs;
      lockInputs = () => { lock0(); vw.lock(); };
      setTimeout(() => vw.focus(), 0);
    } else if (item.input.kind === "number" || item.input.kind === "text") {
      // A unit that is read after the number (45°, 30%) sits after the box; anything else labels the box first.
      const after = !!item.input.label && /^(°|%|°F|°C)$/.test(item.input.label.trim());
      const lab = item.input.label ? `<label for="ans" class="alab">${esc(item.input.label)}</label>` : "";
      ansBox.innerHTML = `<form class="row" id="af" style="margin:0">${after ? "" : lab}<input id="ans" class="cell wide${item.input.kind === "text" ? " text" : ""}" ${item.input.kind === "number" ? 'inputmode="numeric"' : ""} autocomplete="off" aria-label="Your answer">${after ? `<span class="alab" style="margin-left:-4px">${esc(item.input.label!)}</span>` : ""}<button class="btn" type="submit">${ctx.check ? "Next" : "Check"}</button></form>`;
      const inp = ansBox.querySelector<HTMLInputElement>("#ans")!;
      inputs = [inp, ansBox.querySelector("button")!];
      ansBox.querySelector<HTMLFormElement>("#af")!.onsubmit = (e) => { e.preventDefault(); submit(inp.value); if (!answered) { inp.focus(); inp.setSelectionRange(inp.value.length, inp.value.length); } }; // what they typed stays (Meliesa, Nov 4)
      setTimeout(() => inp.focus(), 0);
    } else if (item.input.kind === "choice") {
      ansBox.innerHTML = `<div class="choices" role="group" aria-label="Choose one">${item.input.options.map((c) => `<button class="btn ghost choice" type="button" data-v="${esc(c)}">${esc(c)}</button>`).join("")}</div>`;
      inputs = [...ansBox.querySelectorAll<HTMLButtonElement>("button")];
      inputs.forEach((b) => ((b as HTMLButtonElement).onclick = () => { inputs.forEach((x) => x.classList.remove("picked")); b.classList.add("picked"); submit((b as HTMLButtonElement).dataset.v!); }));
    } else if (tap) {
      const found = new Set(tap.start ?? []);
      ansBox.innerHTML = `<p class="muted" style="margin:0">Found <b id="nf">${found.size}</b> of ${tap.targets.length}.</p>`;
      const cells = [...host.querySelectorAll<HTMLTableCellElement>("#vis td")];
      cells.forEach((td) => {
        td.tabIndex = 0;
        td.setAttribute("role", "button");
        const act = () => {
          if (answered) return;
          const n = Number(td.textContent);
          if (found.has(n)) return;
          if (tap.targets.includes(n)) {
            found.add(n);
            td.classList.add("ring");
            host.querySelector("#nf")!.textContent = String(found.size);
            fb.className = "fb";
            fb.textContent = "";
            if (found.size === tap.targets.length) {
              log(tap.targets.join(","), true, hintsSeen, null);
              fb.className = "fb good";
              fb.innerHTML = item.reveal ?? "That's all of them.";
              done(true);
            }
          } else {
            tries++;
            log(String(n), false, hintsSeen, "tap-wrong");
            fb.className = "fb warn";
            fb.textContent = `Not yet. ${tap.wrongTap ? tap.wrongTap(n, [...found]) : item.hints[0] ?? ""}`;
          }
        };
        td.onclick = act;
        td.onkeydown = (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); act(); } };
      });
    }
    start = performance.now();
  }

  /* ---------- Discover ---------- */
  function drawDiscover(s: Extract<Step, { kind: "discover" }>, box: HTMLElement, head: string) {
    box.innerHTML = `${head}${s.intro ? `<p class="intro">${esc(s.intro)}</p>` : ""}<ol class="trail" id="trail"></ol><div class="card" id="cur"></div><div class="row" id="end"></div>`;
    const trail = $("trail"), cur = $("cur"), end = $("end");
    let i = 0;
    // "Read to me" on the question first reads the teaching just above it: the intro before the first question,
    // then what the last question showed. Each line in the trail has its own button to hear it again.
    const lead = () => (i === 0 ? s.intro ?? "" : plain(trail.lastElementChild?.querySelector(".tr")?.innerHTML ?? ""));
    const one = () => {
      const item = s.items[i]!;
      askItem(item, cur, {
        stepId: s.id,
        lead,
        practice: false,
        onDone: () => {
          const ans = cur.querySelector<HTMLInputElement>("#ans")?.value ?? cur.querySelector<HTMLButtonElement>(".choice.picked")?.dataset.v ?? "";
          const li = document.createElement("li");
          li.innerHTML = `<span class="tq">${item.prompt}</span> <b class="ta">${esc(item.input.kind === "tap" ? "" : ans)}</b><div class="tr">${item.reveal ?? ""}</div>${SAYIT}`;
          setTimeout(() => {
            trail.appendChild(li);
            i++;
            if (i < s.items.length) one();
            else {
              cur.remove();
              finish(s);
              end.innerHTML = nextButton(s);
              wireNext(s);
              root.querySelector<HTMLButtonElement>("#nextStep")?.focus();
            }
          }, 900);
        },
      });
    };
    if (stepDone(P, lesson, s)) {
      // Already discovered: show the path, allow a re-run.
      trail.innerHTML = s.items.map((it) => `<li><span class="tq">${it.prompt}</span><div class="tr">${it.reveal ?? ""}</div>${SAYIT}</li>`).join("");
      cur.innerHTML = `<p class="muted" style="margin:0">You found all of these. <button class="btn ghost small" id="redo" type="button">Do it again</button></p>`;
      $("redo").onclick = () => { trail.innerHTML = ""; i = 0; one(); };
      end.innerHTML = nextButton(s);
      wireNext(s);
      return;
    }
    one();
  }

  /* ---------- Check-up: no hints, no feedback; skip allowed; scored by domain ---------- */
  function drawCheck(s: Extract<Step, { kind: "check" }>, box: HTMLElement, head: string) {
    const cr = ((P.checks ??= {})[s.id] ??= { results: {} });
    const showResults = () => {
      const res = checkScore(s.items, cr.results);
      box.innerHTML = `${head}<div class="card"><p class="check" style="margin-top:0">Done. Thank you.</p>
        <table class="roster"><tr><th>Part</th><th>Right</th></tr>${DOMAINS.filter((d) => res.byDomain[d]!.total).map((d) => `<tr><td>${esc(DOMAIN_NAMES[d]!)}</td><td>${res.byDomain[d]!.right} of ${res.byDomain[d]!.total}</td></tr>`).join("")}
        <tr><td><b>Total</b></td><td><b>${res.right} of ${res.total}</b></td></tr></table>
        <p class="muted">${/pre$/i.test(s.id) ? "Now you know where to begin. Everything you didn't know yet is what the lessons are for." : "Compare this with your first check-up. Every new right answer is your work showing."}</p></div>
        <div class="row"><button class="btn" id="nextStep" type="button">Back to all lessons</button>${o.preview ? '<button class="btn ghost" id="redo" type="button">Take it again (preview)</button>' : ""}</div>`;
      $("nextStep").onclick = o.onBack;
      root.querySelector<HTMLButtonElement>("#redo")?.addEventListener("click", () => { cr.results = {}; delete cr.date; save(); drawCheck(s, box, head); });
    };
    if (cr.date) return showResults();
    let i = s.items.findIndex((it) => !(it.id in cr.results));
    if (i < 0) i = 0;
    box.innerHTML = `${head}${s.intro ? `<p class="intro">${esc(s.intro)}</p>` : ""}<div class="dots" id="dots"></div><div class="card" id="cur"></div>
      <div class="row"><button class="btn ghost small" id="skip" type="button">I don't know this one yet</button><span class="note" id="qn" style="margin:0"></span></div>`;
    const cur = $("cur");
    const dots = () => ($("dots").innerHTML = s.items.map((it) => `<i class="${it.id in cr.results ? "y" : ""}"></i>`).join(""));
    const one = () => {
      if (i >= s.items.length) { cr.date = localDate(); finish(s); return showResults(); }
      const item = s.items[i]!;
      $("qn").textContent = `Question ${i + 1} of ${s.items.length}`;
      dots();
      askItem(item, cur, { stepId: s.id, practice: false, check: true, onDone: (r) => { cr.results[item.id] = r.correct; save(); i++; one(); } });
    };
    $("skip").onclick = () => {
      const item = s.items[i];
      if (!item) return;
      cr.results[item.id] = null;
      void store.logSkillAttempt({ studentId: student.id, lessonId: lesson.id, stepId: s.id, skill: item.skill, itemId: item.id, response: "", correct: false, firstTry: false, hintLevel: 0, misconception: "skipped", ms: 0 }).catch(() => {});
      save();
      i++;
      one();
    };
    one();
  }

  /* ---------- The placement diagnostic ---------- */
  function drawPlacement(box: HTMLElement, head: string) {
    const intro = (lesson.steps[0] as { intro?: string }).intro ?? "";
    let st = startState(student.grade);
    const shell = (title: string) => {
      box.innerHTML = `${head}<p class="intro">${esc(intro)}</p><div class="eyebrow" id="part">${esc(title)}</div><div class="dots" id="dots"></div><div class="card" id="cur"></div>
        <div class="row"><button class="btn ghost small" id="skip" type="button">I don't know this one yet</button><span class="note" id="qn" style="margin:0"></span></div>`;
    };
    const ask = (items: Item[], part: string, done: (res: { correct: boolean; ms: number }[]) => void) => {
      shell(part);
      const res: { correct: boolean; ms: number }[] = [];
      let i = 0, t0 = 0;
      const cur = $("cur");
      const one = () => {
        if (i >= items.length) return done(res);
        $("dots").innerHTML = items.map((_, k) => `<i class="${k < i ? "y" : ""}"></i>`).join("");
        $("qn").textContent = `${i + 1} of ${items.length}`;
        t0 = performance.now();
        askItem(items[i]!, cur, { stepId: "placement", practice: false, check: true, onDone: (r) => { res.push({ correct: r.correct, ms: performance.now() - t0 }); i++; one(); } });
      };
      $("skip").onclick = () => { if (i < items.length) { res.push({ correct: false, ms: 0 }); i++; one(); } };
      one();
    };
    const finishDiag = () => {
      const placed = anyLesson(st.result!)!;
      const ms = [...st.facts.ms].sort((a, b) => a - b);
      P.diagnostic = { date: localDate(), facts: { right: st.facts.right, n: st.facts.n, medianMs: Math.round(ms[Math.floor(ms.length / 2)] ?? 0) }, probes: st.probes, placed: placed.id, solid: !!st.solid, divSolid: !!st.divSolid };
      P.placement = { lesson: placed.id, by: "diagnostic", date: localDate() };
      finish(lesson.steps[0]!);
      save();
      box.innerHTML = `${head}<div class="card"><p class="check" style="margin-top:0">Done. Thank you.</p>
        <p style="font-size:1.15rem">Your starting point: <b>Level ${placed.level}, ${lessonName(placed)} — ${esc(placed.title)}</b>.</p>
        <p class="muted" style="margin-bottom:0">${st.solid === false ? "We start with the times tables — every lesson after them builds on them." : placed.id === TEACHING[0]!.id ? "We start at the beginning, and you'll move as fast as you show you can." : "Everything before it, you showed you already know. It stays open any time you want it."}</p></div>
        <div class="row"><button class="btn" id="startAt" type="button">Start ${lessonName(placed)}</button><button class="btn ghost" id="toAll" type="button">See all my lessons</button></div>`;
      $("startAt").onclick = () => mountLessonView(root, { ...o, lesson: placed });
      $("toAll").onclick = o.onBack;
    };
    const step = () => {
      if (st.phase === "done") return finishDiag();
      const l = PROBE_LESSONS[st.i]!;
      ask(probeItems(l, Math.random), "Lessons", (res) => { st = afterProbe(st, res.filter((r) => r.correct).length); step(); });
    };
    if (st.phase === "facts") ask(factItems(Math.random), "Times tables and division — say each answer out loud, then type it", (res) => { st = afterFacts(st, res); step(); });
    else step();
  }

  /* ---------- The facts check: the method-neutral pre/post measure (lessons/factsTest.ts) ---------- */
  function drawFactsTest(box: HTMLElement, head: string) {
    const intro = (lesson.steps[0] as { intro?: string }).intro ?? "";
    const when: When | null = o.preview && taken(P.factsTest?.pre) && taken(P.factsTest?.post) ? "post" : nextWhen(P);
    const showResults = (w: When) => {
      const r = P.factsTest![w]!, sm = factsSummary(r), other = w === "post" ? P.factsTest!.pre : undefined;
      const line = (name: string, a: { right: number; n: number; fluent: number }, b?: { right: number; n: number; fluent: number }) =>
        `<tr><td>${name}</td><td>${a.right} of ${a.n}</td><td>${a.fluent}</td>${other ? `<td>${b ? `${b.right} of ${b.n}` : ""}</td>` : ""}</tr>`;
      const os = other ? factsSummary(other) : undefined;
      box.innerHTML = `${head}<div class="card"><p class="check" style="margin-top:0">Done. Thank you.</p>
        <table class="roster"><tr><th>${w === "pre" ? "Beginning" : "End"}</th><th>Right</th><th>Under 3 s</th>${other ? "<th>Beginning</th>" : ""}</tr>
        ${line("Multiplication", sm.mul, os?.mul)}${line("Division", sm.div, os?.div)}${line("<b>All</b>", sm.all, os?.all)}</table>
        <p class="muted">${w === "pre" ? "This is where you start. The lessons are for everything you didn't know yet." : "Compare the two columns. Every new right answer is your work showing."}</p></div>
        <div class="row"><button class="btn" id="nextStep" type="button">Back to all lessons</button>${o.preview ? '<button class="btn ghost" id="redo" type="button">Take it again (preview)</button>' : ""}</div>`;
      $("nextStep").onclick = o.onBack;
      root.querySelector<HTMLButtonElement>("#redo")?.addEventListener("click", () => { delete P.factsTest![w]; if (w === "pre") delete P.factsTest!.post; save(); drawFactsTest(box, head); });
    };
    if (!when) return showResults("post");
    if (when === "post" && !o.preview && !taken(P.factsTest?.pre)) return showResults("pre");
    const form = FORM_OF[when], items = formItems(form);
    // Every answer is saved as it's given, so a check cut off by the end of class resumes at the next item (Meliesa, Nov 4).
    const ft = (P.factsTest ??= {});
    const partial = ft[when] && !ft[when]!.date && ft[when]!.form === form ? ft[when]! : undefined;
    const res: Record<string, { c: boolean | null; ms: number }> = partial?.items ?? {};
    if (!partial) ft[when] = { form, date: "", items: res };
    box.innerHTML = `${head}<p class="intro">${esc(intro)}</p>${partial ? '<p class="floor">Picking up where you left off.</p>' : ""}<div class="eyebrow" id="part"></div><div class="dots" id="dots"></div><div class="card" id="cur"></div>
      <div class="row"><button class="btn ghost small" id="skip" type="button">I don't know this one yet</button><span class="note" id="qn" style="margin:0"></span></div>`;
    const cur = $("cur");
    let i = Math.max(0, items.findIndex((it) => !(it.id in res))), t0 = 0;
    if (items.every((it) => it.id in res)) i = items.length;
    const one = () => {
      if (i >= items.length) {
        ft[when] = { form, date: localDate(), items: res };
        finish(lesson.steps[0]!);
        save();
        return showResults(when);
      }
      const item = items[i]!;
      $("part").textContent = item.op === "mul" ? "Multiplication" : "Division";
      $("dots").innerHTML = items.map((_, k) => `<i class="${k < i ? "y" : ""}"></i>`).join("");
      $("qn").textContent = `${i + 1} of ${items.length}`;
      t0 = performance.now();
      askItem(item, cur, { stepId: "factstest", practice: false, check: true, onDone: (r) => { res[item.id] = { c: r.correct, ms: Math.round(performance.now() - t0) }; save(); i++; one(); } });
    };
    $("skip").onclick = () => {
      const item = items[i];
      if (!item) return;
      res[item.id] = { c: null, ms: 0 };
      save();
      void store.logSkillAttempt({ studentId: student.id, lessonId: lesson.id, stepId: "factstest", skill: item.skill, itemId: item.id, response: "", correct: false, firstTry: false, hintLevel: 0, misconception: "skipped", ms: 0 }).catch(() => {});
      i++;
      one();
    };
    one();
  }

  /* ---------- Practice ---------- */
  function drawPractice(s: Extract<Step, { kind: "practice" }>, box: HTMLElement, head: string) {
    const skill = skillById(s.skill), sr = skillRecord(P, s.skill);
    const lead = s.lead?.() ?? [];
    const base = rule(s.mastery);
    // The series comes first, in order; mastery can only be shown after it, on the problems that follow.
    const r = lead.length ? { ...base, min: lead.length + base.min, max: lead.length + base.max } : base;
    let li = 0;
    let block: BlockState = newBlock();
    const seen = new Set<string>();
    box.innerHTML = `${head}${s.intro ? `<p class="intro">${esc(s.intro)}</p>` : ""}
      <div class="dots" id="dots" aria-label="Problems so far"></div><div class="card" id="cur"></div><div class="row" id="end"></div>`;
    const cur = $("cur"), dots = $("dots"), end = $("end");
    const drawDots = () => (dots.innerHTML = block.results.map((x) => `<i class="${x ? "y" : "n"}" title="${x ? "right the first time" : "with help"}"></i>`).join(""));
    const fresh = (): Item => {
      if (li < lead.length) { const it = lead[li++]!; seen.add(it.id); return it; }
      for (let k = 0; k < 20; k++) {
        const it = skill.gen(Math.random, levelFor(block));
        if (!seen.has(it.id)) { seen.add(it.id); return it; }
      }
      return skill.gen(Math.random, levelFor(block));
    };
    const one = () => {
      askItem(fresh(), cur, {
        stepId: s.id,
        practice: true,
        onDone: (res) => {
          block = recordResult(block, res.firstTry, r);
          sr.n++;
          if (res.firstTry) sr.first++;
          drawDots();
          if (block.mastered) {
            if (!sr.mastered) sr.mastered = localDate();
            finish(s);
            setTimeout(() => {
              cur.innerHTML = `<p class="check" style="margin:0">You've got this one.</p>`;
              end.innerHTML = nextButton(s);
              wireNext(s);
              root.querySelector<HTMLButtonElement>("#nextStep")?.focus();
            }, 900);
            return;
          }
          if (block.done) {
            sr.stuck = (sr.stuck ?? 0) + 1;
            save();
            cur.innerHTML = `<p style="margin:0">That's enough of these for today. This one comes back next time, and your teacher can see where it gets tricky.</p>`;
            end.innerHTML = nextButton(s);
            wireNext(s);
            return;
          }
          if (block.needsBackup && s.backup) {
            const back = lesson.steps.find((x) => x.id === s.backup);
            save();
            setTimeout(() => {
              cur.innerHTML = `<p style="margin:0 0 12px">Let's go back and look at the pattern again. Then come back to these.</p><div class="row" style="margin:0"><button class="btn" id="goBack" type="button">Look again: ${esc(back?.title ?? "")}</button><button class="btn ghost" id="keep" type="button">Keep going here</button></div>`;
              $("goBack").onclick = () => back && go(back);
              $("keep").onclick = () => { block = { ...block, needsBackup: false, results: block.results.slice(-1) }; drawDots(); one(); };
            }, 900);
            return;
          }
          save();
          setTimeout(one, res.correct ? 900 : 200);
        },
      });
    };
    if (stepDone(P, lesson, s)) {
      cur.innerHTML = `<p style="margin:0" class="check">Done ✓</p><p class="muted">Practice more any time.</p><button class="btn ghost small" id="more" type="button">Practice more</button>`;
      end.innerHTML = nextButton(s);
      wireNext(s);
      $("more").onclick = () => { end.innerHTML = ""; li = lead.length; one(); };
      return;
    }
    one();
  }

  renderRail();
  renderCheckOut();
  // Below the student's own level, a lesson they haven't started opens with the check, so they skip what they know.
  const fresh = !rec.checkOut && !lesson.steps.some((x) => x.kind !== "soon" && x.kind !== "tables" && stepDone(P, lesson, x));
  if (o.catchUp && !o.preview && fresh && lesson.steps[0]?.kind !== "check" && lesson.steps[0]?.kind !== "placement" && checkable().length) drawCheckOut(checkable());
  else draw();
}

/** Score a check-up by domain. Skipped (null) counts as not yet right. */
export function checkScore(items: CheckItem[], results: Record<string, boolean | null>) {
  const byDomain: Record<string, { right: number; total: number }> = Object.fromEntries(DOMAINS.map((d) => [d, { right: 0, total: 0 }]));
  let right = 0;
  let total = 0;
  for (const it of items) {
    const pts = it.points ?? 1;
    byDomain[it.domain]!.total += pts;
    total += pts;
    if (results[it.id]) { byDomain[it.domain]!.right += pts; right += pts; }
  }
  return { right, total, byDomain };
}
